import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsCardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden animate-pulse">
    <div className="aspect-[16/10] bg-neutral-100" />
    <div className="p-6 space-y-3">
      <div className="h-3 w-1/3 bg-neutral-100 rounded" />
      <div className="h-5 w-4/5 bg-neutral-100 rounded" />
      <div className="h-4 w-full bg-neutral-100 rounded" />
      <div className="h-4 w-2/3 bg-neutral-100 rounded" />
    </div>
  </div>
);

export const NewsPreview = ({ showAll = false }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        setNews(response.data || []);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const displayedNews = showAll ? news : news.slice(0, 3);

  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
          <div>
            <p className="accent-font text-lime-600 mb-4">Latest Updates</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">
              Our <span className="text-lime-600">News</span>
            </h2>
          </div>
          {!showAll && (
            <Link
              to="/news"
              className="btn-secondary inline-flex items-center gap-2 mt-8 lg:mt-0"
              data-testid="news-view-all"
            >
              View All News
              <ArrowRight size={20} weight="bold" />
            </Link>
          )}
        </div>

        {/* News Grid */}
        <div className={`grid md:grid-cols-2 ${showAll ? 'lg:grid-cols-3' : 'lg:grid-cols-3'} gap-8`}>
          {loading ? (
            [0, 1, 2].map(i => <NewsCardSkeleton key={i} />)
          ) : displayedNews.length === 0 ? (
            <div className="col-span-full text-center py-16">
              <p className="text-neutral-500">No news articles yet.</p>
            </div>
          ) : displayedNews.map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="news-card group bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-xl transition-shadow duration-300"
              data-testid={`news-card-${article.id}`}
            >
              <Link to={`/news/${article.slug}`} className="block">
                {/* Image */}
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="news-image w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3 text-neutral-500 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={16} weight="duotone" className="text-lime-600" />
                      <span>{article.date}</span>
                    </div>
                    {article.author && <span className="text-neutral-400">|</span>}
                    {article.author && <span>{article.author}</span>}
                  </div>
                  <h3 className="font-outfit font-semibold text-xl text-neutral-900 group-hover:text-lime-600 transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-neutral-600 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-lime-600 text-sm font-medium pt-2">
                    Read more
                    <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
